import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Icon from "../../../components/AppIcon";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import enGB from "date-fns/locale/en-GB";
import emailjs from "@emailjs/browser";

registerLocale("en-GB", enGB);

// helper to format dd/MM/yyyy for emails
const formatDDMMYYYY = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const ContactForm = ({ apartment, prefillDates }) => {
  const [submitting, setSubmitting] = useState(false);
  const [checkinDate, setCheckinDate] = useState(null);
  const [checkoutDate, setCheckoutDate] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      guests: "",
      purpose: "",
      message: "",
    },
  });

  // Pre-fill dates coming from BookingPanel
  useEffect(() => {
    if (prefillDates?.checkin) setCheckinDate(prefillDates.checkin);
    if (prefillDates?.checkout) setCheckoutDate(prefillDates.checkout);
  }, [prefillDates]);

  const guestsMax = apartment?.capacity || 6;

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const ownerTpl =
        import.meta.env.VITE_EMAILJS_TEMPLATE_OWNER_ID ||
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID; // fallback
      const autoTpl = import.meta.env.VITE_EMAILJS_TEMPLATE_AUTOREPLY_ID; // optional
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !ownerTpl || !publicKey) {
        throw new Error(
          "Missing EmailJS env vars (serviceId/templateId/publicKey)."
        );
      }

      // Build parameters for BOTH templates
      const templateParams = {
        apartment_title: apartment?.title || "-",
        apartment_id: apartment?.id || "-",
        first_name: data.firstName,
        last_name: data.lastName,
        name: `${data.firstName} ${data.lastName}`, // used by autoreply {{name}}
        email: data.email, // used by autoreply "To: {{email}}"
        phone: data.phone || "-",
        guests: data.guests || "-",
        purpose: data.purpose || "-",
        checkin_date: formatDDMMYYYY(checkinDate),
        checkout_date: formatDDMMYYYY(checkoutDate),
        message: data.message || "-",
      };

      // 1) Send to OWNER
      await emailjs.send(serviceId, ownerTpl, templateParams, publicKey);

      // 2) Autoreply to GUEST (if template id provided)
      if (autoTpl) {
        await emailjs.send(serviceId, autoTpl, templateParams, publicKey);
      }

      // Success UI
      reset();
      setCheckinDate(null);
      setCheckoutDate(null);
      alert("Thanks! Your inquiry was sent.");
    } catch (err) {
      console.error(err);
      alert("Sorry, something went wrong sending your message.");
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = new Date();

  return (
    <div className="bg-card border rounded-2xl p-6 md:p-8 shadow-sm">
      <h2 className="text-2xl font-heading font-semibold text-foreground tracking-tight">
        Contact Property Owner
      </h2>
      <p className="text-sm md:text-base text-muted-foreground mt-1">
        Send a message to inquire about availability, pricing, or any questions
        you may have.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
        {/* Names */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Input
            label="First Name *"
            placeholder="First name"
            error={errors?.firstName?.message}
            className="rounded-xl"
            {...register("firstName", { required: "First name is required" })}
          />
          <Input
            label="Last Name *"
            placeholder="Last name"
            error={errors?.lastName?.message}
            className="rounded-xl"
            {...register("lastName", { required: "Last name is required" })}
          />
        </div>

        {/* Contact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Input
            label="Email Address *"
            type="email"
            placeholder="you@example.com"
            error={errors?.email?.message}
            className="rounded-xl"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Enter a valid email",
              },
            })}
          />
          <div>
            <Input
              label="Phone Number"
              type="tel"
              placeholder="+385…"
              error={errors?.phone?.message}
              className="rounded-xl"
              {...register("phone")}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Include country code (e.g., +385 for Croatia)
            </p>
          </div>
        </div>

        {/* Dates (react-datepicker, dd/MM/yyyy, en-GB) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-foreground">
              Check-in Date *
            </label>
            <DatePicker
              selected={checkinDate}
              onChange={(date) => {
                setCheckinDate(date);
              }}
              dateFormat="dd/MM/yyyy"
              locale="en-GB"
              placeholderText="dd/mm/yyyy"
              minDate={minDate}
              className="w-full h-11 px-3 py-2 border border-input bg-background rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-foreground">
              Check-out Date *
            </label>
            <DatePicker
              selected={checkoutDate}
              onChange={(date) => {
                setCheckoutDate(date);
              }}
              dateFormat="dd/MM/yyyy"
              locale="en-GB"
              placeholderText="dd/mm/yyyy"
              minDate={checkinDate || minDate}
              className="w-full h-11 px-3 py-2 border border-input bg-background rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            />
          </div>
        </div>

        {/* Guests & Purpose */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Number of Guests *
            </label>
            <select
              defaultValue=""
              className="w-full h-11 px-3 text-sm bg-background border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              {...register("guests", { required: "Please select guests" })}
            >
              <option value="" disabled>
                Select guests
              </option>
              {Array.from({ length: guestsMax }).map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1} {i === 0 ? "guest" : "guests"}
                </option>
              ))}
            </select>
            {errors?.guests && (
              <p className="text-xs text-red-500 mt-1">
                {errors.guests.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Purpose of Stay
            </label>
            <select
              defaultValue=""
              className="w-full h-11 px-3 text-sm bg-background border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              {...register("purpose")}
            >
              <option value="" disabled>
                Select purpose
              </option>
              <option value="vacation">Vacation</option>
              <option value="business">Business</option>
              <option value="family">Family visit</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Message
          </label>
          <textarea
            rows={5}
            placeholder="Tell us about your stay requirements..."
            className="w-full px-3 py-2 text-sm bg-background border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            {...register("message")}
          />
        </div>

        {/* Submit */}
        <Button
          type="submit"
          onClick={handleSubmit(onSubmit)}
          className="w-full h-11 rounded-xl shadow-sm hover:shadow transition-shadow"
          disabled={submitting}
        >
          <span className="inline-flex items-center gap-2">
            <Icon name="Send" size={16} />
            {submitting ? "Sending…" : "Send Inquiry"}
          </span>
        </Button>
      </form>

      {/* Footer */}
      <div className="mt-6 pt-6 border-t text-center">
        <p className="text-sm text-muted-foreground">Or contact us directly</p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-sm text-gray-700">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="Phone" size={16} className="shrink-0" />
            <a
              href={`tel:${apartment?.host?.phone || ""}`}
              className="min-w-0 break-all hover:underline"
            >
              {apartment?.host?.phone || "+385 95 905 2151"}
            </a>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="Mail" size={16} className="shrink-0" />
            <a
              href={`mailto:${apartment?.host?.email || ""}`}
              className="min-w-0 break-all hover:underline"
            >
              {apartment?.host?.email || "sonja.drglin@gmail.com"}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
